import React from "react";
import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./auth/AuthContext";
import { AuthCallback } from "./auth/AuthCallback";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { Landing } from "./pages/Landing";
import { Dashboard } from "./pages/Dashboard";
import { ProfileSetup } from "./pages/ProfileSetup";
import { LegalAcceptance } from "./pages/LegalAcceptance";
import { Pending } from "./pages/Pending";
import { Upgrade } from "./pages/Upgrade";
import { Settings, Reports } from "./pages/UserPages";
import { ZerodhaSetupGuide } from "./pages/ZerodhaSetupGuide";
import { AdminUsers, AdminConfiguration, AdminAlerts, AdminSubscriptions } from "./pages/Admin";
import { AdminJobs } from "./pages/AdminJobs";
import { BacktestsPage } from "./pages/BacktestsPage";
import JournalPage from "./pages/JournalPage";
import MomentumScannerPage from "./pages/MomentumScannerPage";
import LinearAccumulationPage from "./pages/LinearAccumulation";
import PortfolioPage from "./pages/PortfolioPage";
import { TradeTerminal } from "./pages/TradeTerminal";
import SetupG from "./pages/SetupG";
import { AppShell } from "./pages/AppShell";
import Architecture from "./components/Architecture";
import CommoditiesOverview from "./pages/commodities/CommoditiesOverview";
import CommoditiesStrategies from "./pages/commodities/CommoditiesStrategies";
import CommoditiesSignals from "./pages/commodities/CommoditiesSignals";
import CommoditiesPaperTrading from "./pages/commodities/CommoditiesPaperTrading";
import CommoditiesPnL from "./pages/commodities/CommoditiesPnL";
import CommoditiesJournal from "./pages/commodities/CommoditiesJournal";
import CommoditiesExecution from "./pages/commodities/CommoditiesExecution";
import CommoditiesConfiguration from "./pages/commodities/CommoditiesConfiguration";
import PaperFanoutComparison from "./pages/PaperFanoutComparison";
import LiveOrders from "./pages/LiveOrders";

const AppRouter = () => {
  const location = useLocation();
  // CRITICAL: Synchronous detection of session_id in URL fragment.
  // useEffect runs AFTER first render — too late to prevent ProtectedRoute races.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  const Shell = ({ children, ...guards }) => (
    <ProtectedRoute {...guards}>
      <AppShell>{children}</AppShell>
    </ProtectedRoute>
  );

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/profile-setup"
             element={<ProtectedRoute requireActive={false}><ProfileSetup /></ProtectedRoute>} />
      <Route path="/legal-acceptance"
             element={<ProtectedRoute requireActive={false}><LegalAcceptance /></ProtectedRoute>} />
      <Route path="/pending"
             element={<ProtectedRoute requireActive={false}><Pending /></ProtectedRoute>} />
      <Route path="/upgrade" element={<Shell><Upgrade /></Shell>} />
      <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
      <Route path="/settings" element={<Shell><Settings /></Shell>} />
      <Route path="/settings/zerodha-setup-guide" element={<Shell><ZerodhaSetupGuide /></Shell>} />
      <Route path="/reports" element={<Shell><Reports /></Shell>} />
      {/* 2026-09-13 — chat: "Backtests, Journal, Momentum scanner, Steady,
          portfolio should be available only for Admin (as most of them are
          still in development)." All five gated ADMIN-only; NavItems in
          AppShell.jsx moved under the isAdmin block to match. */}
      <Route path="/backtests" element={<Shell requireRole={["ADMIN"]}><BacktestsPage /></Shell>} />
      <Route path="/journal" element={<Shell requireRole={["ADMIN"]}><JournalPage /></Shell>} />
      <Route path="/momentum" element={<Shell requireRole={["ADMIN"]}><MomentumScannerPage /></Shell>} />
      <Route path="/steady-intraday-trend" element={<Shell requireRole={["ADMIN"]}><LinearAccumulationPage /></Shell>} />
      {/* Legacy alias — redirects handled inside LinearAccumulationPage */}
      <Route path="/linear-accumulation" element={<Shell requireRole={["ADMIN"]}><LinearAccumulationPage /></Shell>} />
      <Route path="/portfolio" element={<Shell requireRole={["ADMIN"]}><PortfolioPage /></Shell>} />
      {/* Commodities (MCX) — new, dedicated top-level menu (2026-08-21) */}
      <Route path="/commodities" element={<Shell><CommoditiesOverview /></Shell>} />
      <Route path="/commodities/strategies" element={<Shell><CommoditiesStrategies /></Shell>} />
      <Route path="/commodities/signals" element={<Shell><CommoditiesSignals /></Shell>} />
      {/* 2026-09-13 — chat: "Commodities: Paper Trading for Admin only." —
          previously open to every user like the other Commodities screens. */}
      <Route path="/commodities/paper-trading" element={<Shell requireRole={["ADMIN"]}><CommoditiesPaperTrading /></Shell>} />
      <Route path="/commodities/pnl" element={<Shell><CommoditiesPnL /></Shell>} />
      <Route path="/commodities/journal" element={<Shell requireRole={["ADMIN"]}><CommoditiesJournal /></Shell>} />
      {/* 2026-09-13 — chat: "Execution (make it Live Orders) and should be
          user specific." — no longer admin-only; CommoditiesExecution.jsx
          itself now shows the admin control panel only to ADMIN viewers and
          a user-scoped Live Orders table to everyone (see its own comment). */}
      <Route path="/commodities/execution" element={<Shell><CommoditiesExecution /></Shell>} />
      <Route path="/commodities/configuration" element={<Shell requireRole={["ADMIN"]}><CommoditiesConfiguration /></Shell>} />
      {/* 2026-10-26 — unified Manual Order (stocks + commodities, every Kite account the
          viewer owns; admin: all). Backend scopes each account via kite_accounts.can_access. */}
      <Route path="/manual-order" element={<Shell><TradeTerminal /></Shell>} />
      {/* 2026-11-01 — Setup G · EMA 9/21 Reversal (NIFTY options) paper forward test */}
      <Route path="/setup-g" element={<Shell requireRole={["ADMIN"]}><SetupG /></Shell>} />
      <Route path="/admin/paper-fanout" element={<Shell requireRole={["ADMIN"]}><PaperFanoutComparison /></Shell>} />
      <Route path="/live-orders" element={<Shell><LiveOrders /></Shell>} />
      <Route path="/admin/users" element={<Shell requireRole={["ADMIN"]}><AdminUsers /></Shell>} />
      <Route path="/admin/subscriptions" element={<Shell requireRole={["ADMIN"]}><AdminSubscriptions /></Shell>} />
      <Route path="/admin/configuration" element={<Shell requireRole={["ADMIN"]}><AdminConfiguration /></Shell>} />
      <Route path="/admin/alerts" element={<Shell requireRole={["ADMIN"]}><AdminAlerts /></Shell>} />
      <Route path="/admin/jobs" element={<Shell requireRole={["ADMIN"]}><AdminJobs /></Shell>} />
      <Route path="/architecture" element={<Architecture />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
